import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NumberField, { NumberFieldProps } from "../../common/NumberField";

export const inlineInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "background.paper",
  },
} as const;

/** Compact text input used inside an InlineField. */
export const InlineText: React.FC<TextFieldProps> = ({ sx, ...props }) => (
  <TextField
    size="small"
    fullWidth
    variant="outlined"
    {...props}
    sx={[inlineInputSx, ...(Array.isArray(sx) ? sx : [sx])]}
  />
);

/**
 * Number input: keeps the value as a string, like the rest of the drafts.
 * Accepts comma or dot as decimal separator (see NumberField).
 */
export const InlineNumber: React.FC<NumberFieldProps> = ({ sx, ...props }) => (
  <NumberField
    size="small"
    fullWidth
    variant="outlined"
    {...props}
    sx={[inlineInputSx, ...(Array.isArray(sx) ? sx : [sx])]}
  />
);

export interface SelectOption {
  value: string;
  label: React.ReactNode;
}

type InlineSelectProps = Omit<TextFieldProps, "onChange" | "select"> & {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  /** Label of an empty option; omitted → no empty option. */
  emptyLabel?: string;
};

export const InlineSelect: React.FC<InlineSelectProps> = ({
  value,
  options,
  onChange,
  emptyLabel,
  ...props
}) => (
  <InlineText
    select
    value={value}
    onChange={(event) => onChange(event.target.value)}
    {...props}
  >
    {emptyLabel !== undefined && (
      <MenuItem value="">
        <em>{emptyLabel}</em>
      </MenuItem>
    )}
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </InlineText>
);

interface SectionEmptyProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** Empty state of a section whose record doesn't exist yet. */
export const SectionEmpty: React.FC<SectionEmptyProps> = ({
  message,
  actionLabel,
  onAction,
}) => (
  <Box
    sx={{
      border: "1px dashed",
      borderColor: "divider",
      borderRadius: 2,
      px: 2,
      py: 3,
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
    >
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={onAction}
          sx={{ flexShrink: 0 }}
        >
          {actionLabel}
        </Button>
      )}
    </Stack>
  </Box>
);
